import { motion } from "framer-motion";
import { MessageCircle, Mail, Clock, MapPin, Facebook, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND, CONTACT_EMAIL, CONTACT_HOURS, buildTelegramLink } from "@/config";

const TelegramIcon = ({ className = "h-7 w-7" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
  </svg>
);

const LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_research-precision-1/artifacts/0d2556841c9f64b9_logo%20lighter.jpeg";

export const Contact = () => {
  const telegramHref = buildTelegramLink(`Hello ${BRAND.name} team, I have an enquiry.`);

  return (
    <section id="contact" className="relative py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-5">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="lg:col-span-2">
            <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-slate-500">Get in touch</span>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Enquiries — usually answered same day.</h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              Tell us what you need. Our UK team will come back to you quickly with clear, honest information about the right resource for your work.
            </p>

            <div className="mt-8 space-y-4">
              <a href={telegramHref} target="_blank" rel="noreferrer" data-testid="telegram-contact-button" className="flex items-center gap-3 rounded-xl glass metallic-border p-4 transition-colors hover:border-[#229ED9]/40">
                <span className="grid h-10 w-10 place-items-center rounded-lg bg-[#229ED9]/10 text-[#229ED9]"><TelegramIcon className="h-5 w-5" /></span>
                <div><p className="text-sm font-medium text-slate-900">Telegram</p><p className="text-xs text-slate-500">Chat with support directly</p></div>
              </a>
              <div className="flex items-center gap-3 rounded-xl glass metallic-border p-4">
                <span className="grid h-10 w-10 place-items-center rounded-lg bg-slate-100 text-slate-700"><Mail className="h-5 w-5" /></span>
                <div><p className="text-sm font-medium text-slate-900">Email</p><p className="text-xs text-slate-500">{CONTACT_EMAIL}</p></div>
              </div>
              <div className="flex items-center gap-3 rounded-xl glass metallic-border p-4">
                <span className="grid h-10 w-10 place-items-center rounded-lg bg-slate-100 text-slate-700"><Clock className="h-5 w-5" /></span>
                <div><p className="text-sm font-medium text-slate-900">Support Hours</p><p className="text-xs text-slate-500">{CONTACT_HOURS}</p></div>
              </div>
              <div className="flex items-center gap-3 rounded-xl glass metallic-border p-4">
                <span className="grid h-10 w-10 place-items-center rounded-lg bg-slate-100 text-slate-700"><MapPin className="h-5 w-5" /></span>
                <div><p className="text-sm font-medium text-slate-900">Areas covered</p><p className="text-xs text-slate-500">United Kingdom · nationwide dispatch</p></div>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <span className="text-xs text-slate-500">Follow</span>
              <a href="#" className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-700 hover:border-slate-400 hover:text-slate-900"><Facebook className="h-4 w-4" /></a>
              <a href="#" className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-700 hover:border-slate-400 hover:text-slate-900"><Instagram className="h-4 w-4" /></a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-3 flex flex-col items-center justify-center rounded-2xl glass-strong metallic-border p-8 text-center sm:p-12"
          >
            <div className="flex items-center justify-center gap-4">
              <span className="h-16 w-16 overflow-hidden rounded-full ring-2 ring-slate-200 shadow-sm">
                <img src={LOGO} alt="PepsWorld UK" className="h-full w-full scale-[1.18] object-cover" />
              </span>
              <span className="font-display text-xl font-light text-slate-400">×</span>
              <span className="grid h-16 w-16 place-items-center rounded-full bg-[#229ED9] text-white shadow-[0_8px_20px_-6px_rgba(34,158,217,0.7)]">
                <TelegramIcon className="h-8 w-8" />
              </span>
            </div>
            <h3 className="mt-6 font-display text-2xl font-semibold text-slate-900">Message us on Telegram</h3>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-600">
              The fastest way to reach us. Tap below and our UK team will reply directly — no forms, no waiting.
            </p>
            <a href={telegramHref} target="_blank" rel="noreferrer" data-testid="contact-telegram-cta" className="mt-7 w-full max-w-xs">
              <Button className="h-12 w-full bg-[#229ED9] font-semibold text-white hover:bg-[#1b8ec4]">
                <MessageCircle className="mr-2 h-5 w-5" /> Message us on Telegram
              </Button>
            </a>
            <p className="mt-4 font-mono2 text-xs text-slate-500">@pepsworlduk · t.me/pepsworlduk</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
